import * as fs from "node:fs";
import * as net from "node:net";
import * as os from "node:os";
import * as path from "node:path";
import { randomBytes } from "node:crypto";
import { spawn, spawnSync } from "node:child_process";
import type { TokenCredential } from "@azure/identity";
import { resolveFoundryProject } from "./foundry-project.js";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";
import { grantFoundryUser } from "./rbac.js";
import { tryOpenUrl } from "./gh-helpers.js";
import { deployIntakeAgent } from "./intake-agent.js";

export interface FoundryEndpointInfo { endpoint: string; accountScope: string }

function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function isAuthorizationShapedError(error: unknown): boolean {
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current != null && !seen.has(current)) {
    seen.add(current);
    if (typeof current === "object") {
      const record = current as { statusCode?: unknown; message?: unknown; cause?: unknown };
      if (record.statusCode === 403) return true;
      if (typeof record.message === "string" && /403|Forbidden|PermissionDenied|not authorized/i.test(record.message)) return true;
      current = record.cause;
      continue;
    }
    if (typeof current === "string" && /403|Forbidden|PermissionDenied|not authorized/i.test(current)) return true;
    break;
  }
  return false;
}

export async function deployIntakeAgentWithRetry(args: {
  credential: TokenCredential;
  endpoint: string;
  repoRoot: string;
  /** Model chosen by the cascade. Omitted → the persona frontmatter floor. */
  model?: string;
  fieldOverrides?: Record<string, string>;
  onWait?: (msg: string) => void;
  maxWaitMs?: number;
  intervalMs?: number;
}): Promise<string> {
  const maxWaitMs = args.maxWaitMs ?? 300_000;
  const intervalMs = args.intervalMs ?? 10_000;
  const deadline = Date.now() + maxWaitMs;

  for (;;) {
    try {
      return await deployIntakeAgent({
        credential: args.credential,
        endpoint: args.endpoint,
        repoRoot: args.repoRoot,
        model: args.model,
        fieldOverrides: args.fieldOverrides,
      });
    } catch (error) {
      if (!isAuthorizationShapedError(error)) throw error;
      const remainingMs = deadline - Date.now();
      if (remainingMs <= 0) {
        throw new LocalCliError({
          code: "BOOTSTRAP_UI_ROLE_PROPAGATION_TIMEOUT",
          message: "Timed out waiting for Azure role propagation before deploying the intake agent.",
          hint: "Azure role propagation can take a few minutes - re-run 'm8t bootstrap ui' (idempotent).",
          cause: error,
        });
      }
      args.onWait?.("waiting for Azure role propagation...");
      await sleep(Math.min(intervalMs, remainingMs));
    }
  }
}

/**
 * Poll for the Foundry project the cloud installer creates in the founder's
 * subscription. resolveFoundryProject throws while no AIServices project exists
 * yet (the installer hasn't reached its foundry-create phase) — we tolerate that
 * window and retry until one appears or we time out.
 *
 * Only retries on FOUNDRY_PROJECT_ZERO (no project yet). Any other error (auth
 * failure, config issue, etc.) is re-thrown immediately so it isn't masked as a
 * misleading "Foundry is not ready" timeout.
 */
export async function resolveFoundryEndpointWithWait(
  args: { credential: TokenCredential; subscriptionId: string; endpoint?: string },
  opts: { pollMs?: number; timeoutMs?: number; onWait?: (msg: string) => void } = {},
): Promise<FoundryEndpointInfo> {
  const pollMs = opts.pollMs ?? 10_000;
  const timeoutMs = opts.timeoutMs ?? 15 * 60_000;
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    try {
      const p = await resolveFoundryProject({
        credential: args.credential,
        subscriptionId: args.subscriptionId,
        interactive: false,
        // When the subscription holds multiple projects (e.g. a founder reinstalling
        // alongside an existing platform), an explicit endpoint disambiguates instead
        // of FOUNDRY_PROJECT_MULTIPLE. Omitted → single-project auto-discovery.
        endpoint: args.endpoint,
      });
      return { endpoint: p.endpoint, accountScope: p.accountScope };
    } catch (e) {
      if (e instanceof LocalCliError && e.code === "FOUNDRY_PROJECT_MULTIPLE") {
        throw new LocalCliError({
          code: "BOOTSTRAP_UI_MULTIPLE_PROJECTS",
          message: `${e.message} bootstrap ui can't pick one safely (it must not deploy the intake agent into the wrong project).`,
          hint: "Re-run with the throwaway project's endpoint, e.g. 'm8t bootstrap ui --repo-root \"$(pwd)\" --endpoint https://<account>.services.ai.azure.com/api/projects/m8t'. The installer prints it in 'm8t bootstrap status'.",
          cause: e,
        });
      }
      if (!(e instanceof LocalCliError && e.code === "FOUNDRY_PROJECT_ZERO")) {
        throw e;
      }
      if (Date.now() >= deadline) {
        throw new LocalCliError({
          code: "BOOTSTRAP_UI_FOUNDRY_NOT_READY",
          message: "Foundry is not ready yet — no AI project appeared in your subscription before the timeout.",
          hint: "Check 'm8t bootstrap status --watch'; the install may have stalled in foundry-create.",
          cause: e,
        });
      }
      opts.onWait?.("waiting for Foundry-create to finish…");
      await sleep(pollMs);
    }
  }
}

/** OID of the signed-in user (for the role assignment). */
export async function getSignedInUserOid(): Promise<string> {
  const oid = (await runAz(["ad", "signed-in-user", "show", "--query", "id", "-o", "tsv"])).trim();
  if (!oid) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_NO_SIGNED_IN_USER",
      message: "Could not resolve the signed-in user (az ad signed-in-user show returned nothing).",
      hint: "Personal Microsoft accounts can't be resolved this way — sign in with a work/school account.",
    });
  }
  return oid;
}

/**
 * Grants the founder the Foundry data-plane role on the account (idempotent).
 * Delegates to rbac.ts grantFoundryUser (REST PUT, okStatuses: [409]) so re-runs
 * are safe — az role assignment create is NOT reliably idempotent (409 exits non-zero).
 *
 * If the live smoke shows prompt-agent createVersion/invoke still 403s after RBAC
 * propagation, escalate by additionally granting a broader role (e.g. Cognitive Services
 * Contributor / Azure AI Developer).
 */
export async function ensureFounderFoundryRole(args: {
  credential: TokenCredential;
  subscriptionId: string;
  principalId: string;
  accountScope: string;
}): Promise<void> {
  await grantFoundryUser({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    accountScope: args.accountScope,
    principalId: args.principalId,
    principalType: "User",
  });
}

/**
 * Write apps/web/.env.local for the local onboarding run. Text-first by default:
 * the intake voice stack is off unless the run passed `--voice`, in which case the
 * relay pointer and the intake voice flag are written together — one switch, so the
 * relay process and the web surface can't disagree about whether voice is on for any
 * run that actually serves. (`--prep-only --voice` is the one exception: it writes
 * the voice env vars but starts no relay, since `--prep-only` starts no processes
 * at all — see apps/cli/src/commands/bootstrap/ui.ts's prepOnly early return.)
 *
 * NEXT_PUBLIC_M8T_INTAKE_VOICE is consumed by apps/web/lib/intake-voice.ts — rename
 * it here and you must rename it there too, or the CLI suite stays green while
 * writing a flag nobody reads.
 *
 * STORAGE_ACCOUNT_NAME is left EMPTY so the gateway boot is skipped; the webapp
 * forwards the browser MSAL token to Foundry — no platform deps. Backs up any
 * existing file to .env.local.bak so a developer's local config isn't silently
 * clobbered.
 *
 * ⚠️ NEXT_PUBLIC_* is inlined at compile time, so this MUST be written before the
 * web server is spawned (see serveOnboardingUiDetached's caller).
 */
export function writeWebEnvLocal(args: {
  repoRoot: string; tenantId: string; clientId: string; foundryEndpoint: string;
  /** True when this run passed --voice: start the relay and let the intake surface show voice. */
  voice: boolean;
  randomBytesImpl?: (size: number) => Buffer;
}): string {
  const envPath = path.join(args.repoRoot, "apps", "web", ".env.local");
  let prior = "";
  if (fs.existsSync(envPath)) {
    prior = fs.readFileSync(envPath, "utf8");
    fs.copyFileSync(envPath, envPath + ".bak");
  }
  const priorSecret = readValidInternalSecret(prior);
  const internalSecret = priorSecret ?? (args.randomBytesImpl ?? randomBytes)(32).toString("base64url");
  if (!isValidInternalSecret(internalSecret)) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_INTERNAL_SECRET_GENERATION_FAILED",
      message: "Could not generate a valid local internal service secret.",
    });
  }
  const body = [
    "# Written by `m8t bootstrap ui` — local onboarding run (the onboarding intake agent).",
    "# Text-first: the intake voice stack is off unless the run passed --voice.",
    "# STORAGE unset → gateway boot skipped; the webapp forwards your MSAL token to Foundry.",
    `AZURE_TENANT_ID=${args.tenantId}`,
    `AZURE_CLIENT_ID=${args.clientId}`,
    `FOUNDRY_PROJECT_ENDPOINT=${args.foundryEndpoint}`,
    ...(args.voice
      ? ["VOICE_RELAY_PUBLIC_URL=ws://localhost:8790", "NEXT_PUBLIC_M8T_INTAKE_VOICE=1"]
      : []),
    "STORAGE_ACCOUNT_NAME=",
    `M8T_INTERNAL_SECRET=${internalSecret}`,
    "M8T_SINGLE_GATEWAY_PROCESS=1",
    "M8T_DISABLE_WORKER=1",
    "M8T_ONBOARDING=1",
    "NEXT_PUBLIC_M8T_ONBOARDING=1",
    "",
  ].join("\n");
  fs.writeFileSync(envPath, body, { encoding: "utf8", mode: 0o600 });
  try { fs.chmodSync(envPath, 0o600); } catch { /* best-effort on filesystems without POSIX modes */ }
  return envPath;
}

function isValidInternalSecret(value: string | undefined): value is string {
  if (!value || !/^[A-Za-z0-9_-]{43}$/.test(value)) return false;
  const decoded = Buffer.from(value, "base64url");
  return decoded.length === 32 && decoded.toString("base64url") === value;
}

function readValidInternalSecret(envText: string): string | undefined {
  const values = envText.split(/\r?\n/)
    .filter((line) => line.startsWith("M8T_INTERNAL_SECRET="))
    .map((line) => line.slice("M8T_INTERNAL_SECRET=".length));
  if (values.length !== 1) return undefined;
  return isValidInternalSecret(values[0]) ? values[0] : undefined;
}

function readWebInternalSecret(repoRoot: string): string {
  const envPath = path.join(repoRoot, "apps", "web", ".env.local");
  let secret: string | undefined;
  try {
    secret = readValidInternalSecret(fs.readFileSync(envPath, "utf8"));
  } catch {
    secret = undefined;
  }
  if (!secret) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_INTERNAL_SECRET_MISSING",
      message: "The local web environment has no valid internal service secret.",
      hint: "Re-run 'm8t bootstrap ui' so the web app and voice relay receive the same secret.",
    });
  }
  return secret;
}

/** Throw a clear error if the Node major is < 20 (apps/web needs Node 20+). */
export function assertNodeVersion(versionString: string = process.version): void {
  const major = Number(/^v?(\d+)\./.exec(versionString)?.[1] ?? "0");
  if (major < 20) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_NODE_TOO_OLD",
      message: `apps/web needs Node 20+, but this is ${versionString}.`,
      hint: "Install Node 20+ (see install/prereqs-*.md), then re-run 'm8t bootstrap ui'.",
    });
  }
}

/** Run a command in `cwd`, inheriting stdio, rejecting with a LocalCliError on non-zero. */
export function runInherit(cmd: string, cmdArgs: string[], cwd: string, failCode: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn(cmd, cmdArgs, { cwd, stdio: "inherit", shell: process.platform === "win32" });
    child.on("error", (e) => { reject(new LocalCliError({ code: failCode, message: `Failed to start '${cmd}': ${e.message}` })); });
    child.on("close", (code) => {
      if (code === 0) {
        resolve();
      } else {
        const opts: { code: string; message: string; hint?: string } = {
          code: failCode,
          message: `'${cmd} ${cmdArgs.join(" ")}' exited with code ${String(code)}.`,
        };
        if (cmd === "pnpm") {
          opts.hint = "Fix the pnpm error above, then re-run 'm8t bootstrap ui' (it's idempotent).";
        }
        reject(new LocalCliError(opts));
      }
    });
  });
}

/** corepack enable + pnpm install at the repo root (installs the whole workspace). */
export async function installWebDeps(repoRoot: string): Promise<void> {
  assertNodeVersion();
  await runInherit("corepack", ["enable"], repoRoot, "BOOTSTRAP_UI_COREPACK_FAILED").catch(() => {
    // corepack enable can need elevated perms on some setups; pnpm may already be on PATH.
    // Non-fatal — let pnpm install surface a real "pnpm not found" if it truly isn't there.
  });
  await runInherit("pnpm", ["install"], repoRoot, "BOOTSTRAP_UI_INSTALL_FAILED");
}

/** Paths used by the detached onboarding-UI process helpers. */
export function onboardingUiPaths(home: string = os.homedir()): { logPath: string; pidPath: string } {
  const dir = path.join(home, ".m8t");
  return {
    logPath: path.join(dir, "onboarding-ui.log"),
    pidPath: path.join(dir, "onboarding-ui.pid"),
  };
}

/** Paths used by the detached onboarding voice-relay process helpers. */
export function onboardingRelayPaths(home: string = os.homedir()): { logPath: string; pidPath: string } {
  const dir = path.join(home, ".m8t");
  return {
    logPath: path.join(dir, "onboarding-relay.log"),
    pidPath: path.join(dir, "onboarding-relay.pid"),
  };
}

function isLocalPortOpen(port: number): Promise<boolean> {
  return new Promise((resolve) => {
    const s = net.createConnection({ host: "127.0.0.1", port });
    s.setTimeout(1000);
    s.on("connect", () => { s.destroy(); resolve(true); });
    s.on("error", () => { resolve(false); });
    s.on("timeout", () => { s.destroy(); resolve(false); });
  });
}

function removePidFile(pidPath: string): void {
  try { fs.unlinkSync(pidPath); } catch { /* absent or already removed */ }
}

function parseManagedPid(text: string): number | null {
  if (!/^[1-9]\d*$/.test(text)) return null;
  const pid = Number(text);
  if (!Number.isSafeInteger(pid) || pid < 2 || pid === process.pid || pid === process.ppid) return null;
  return pid;
}

/** Return a live pid we previously recorded; remove malformed/dead stale pid files. */
function readLiveManagedPid(pidPath: string): number | null {
  let text: string;
  try {
    text = fs.readFileSync(pidPath, "utf8").trim();
  } catch {
    return null;
  }
  const pid = parseManagedPid(text);
  if (pid == null) {
    removePidFile(pidPath);
    return null;
  }
  try {
    process.kill(pid, 0);
    return pid;
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    if (code === "ESRCH") {
      removePidFile(pidPath);
      return null;
    }
    if (code === "EPERM") return pid;
    throw error;
  }
}

/**
 * Start `pnpm --filter web dev` detached so it survives the CLI exit.
 *
 * - If the port is already serving, returns immediately with `alreadyRunning: true`
 *   so we never spawn a second dev server.
 * - Otherwise spawns with `detached: true` + `child.unref()` — the child becomes a
 *   process-group leader; `stopOnboardingUi` kills the whole group via `process.kill(-pid)`
 *   on POSIX, or `taskkill /T` on Windows (no process-group concept there).
 * - Polls readiness up to ~45 s (500 ms intervals). If the server hasn't answered by
 *   then, returns anyway — it's still compiling; the caller prints a "check logs" note.
 */
export async function serveOnboardingUiDetached(args: {
  repoRoot: string;
  port: string;
}): Promise<{ alreadyRunning: boolean; logPath: string }> {
  const { logPath, pidPath } = onboardingUiPaths();
  const portNum = Number(args.port);

  // Check if something is already serving on the port.
  const isPortOpen = () => isLocalPortOpen(portNum);
  const portOpen = await isPortOpen();
  const managedPid = readLiveManagedPid(pidPath);

  if (portOpen && managedPid != null) {
    return { alreadyRunning: true, logPath };
  }
  if (portOpen) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_PORT_IN_USE",
      message: `Port ${args.port} is occupied by a process not started by m8t bootstrap.`,
      hint: `Stop the process using port ${args.port}, then re-run 'm8t bootstrap ui'.`,
    });
  }
  // A tracked process with no listening socket is most likely still starting.
  // Do not launch a duplicate or kill a process we cannot safely identify further.
  if (managedPid != null) return { alreadyRunning: true, logPath };

  // Open log file (append) and spawn detached.
  fs.mkdirSync(path.dirname(logPath), { recursive: true });
  const fd = fs.openSync(logPath, "a");
  const child = spawn("pnpm", ["--filter", "web", "dev"], {
    cwd: args.repoRoot,
    detached: true,
    stdio: ["ignore", fd, fd],
    env: { ...process.env, PORT: args.port, M8T_SINGLE_GATEWAY_PROCESS: "1" },
    // pnpm resolves to pnpm.cmd on Windows, which needs a shell to launch.
    shell: process.platform === "win32",
  });
  fs.closeSync(fd);
  // A synchronous spawn failure (e.g. pnpm not on PATH) leaves pid undefined —
  // fail loudly here instead of writing "undefined" to the pid file and then
  // polling for 45 s on a server that will never come up.
  if (child.pid == null) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_DEV_SPAWN_FAILED",
      message: "Could not start the web dev server (pnpm did not spawn).",
      hint: "Check that pnpm + Node 20+ are installed; see the log at " + logPath + ".",
    });
  }
  child.unref();

  // Persist the pid so --stop can kill the process group later.
  fs.writeFileSync(pidPath, String(child.pid), "utf8");

  // Poll readiness (up to ~45 s).
  const deadline = Date.now() + 45_000;
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  while (Date.now() < deadline) {
    await sleep(500);
    if (await isPortOpen()) break;
  }

  return { alreadyRunning: false, logPath };
}

/** Start the voice relay detached so it survives the CLI exit. */
export async function serveOnboardingRelayDetached(args: {
  repoRoot: string;
  foundryEndpoint: string;
  tenantId: string;
  clientId: string;
  webInternalBaseUrl: string;
}): Promise<{ alreadyRunning: boolean; logPath: string }> {
  const { logPath, pidPath } = onboardingRelayPaths();
  const portNum = 8790;
  if (args.webInternalBaseUrl !== "http://localhost:3000") {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_INTERNAL_BASE_URL_INVALID",
      message: "The local voice relay internal base URL must be http://localhost:3000.",
    });
  }
  const internalSecret = readWebInternalSecret(args.repoRoot);
  const portOpen = await isLocalPortOpen(portNum);
  const managedPid = readLiveManagedPid(pidPath);

  if (portOpen && managedPid != null) {
    return { alreadyRunning: true, logPath };
  }
  if (portOpen) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_RELAY_PORT_IN_USE",
      message: "Port 8790 is occupied by a process not started by m8t bootstrap.",
      hint: "Stop the process using port 8790, then re-run 'm8t bootstrap ui'.",
    });
  }
  if (managedPid != null) return { alreadyRunning: true, logPath };

  fs.mkdirSync(path.dirname(logPath), { recursive: true });
  const fd = fs.openSync(logPath, "a");
  // Use `tsx voice-relay-entry.ts`, not `pnpm start:relay`: `start:relay`
  // runs the prod esbuild bundle under `.next/standalone`, which does not
  // exist during `pnpm dev`; detached Node processes do not load
  // `apps/web/.env.local`, so `loadConfig()` receives these values from `env`.
  const child = spawn("pnpm", ["--filter", "web", "exec", "tsx", "voice-relay-entry.ts"], {
    cwd: args.repoRoot,
    detached: true,
    stdio: ["ignore", fd, fd],
    env: {
      ...process.env,
      FOUNDRY_PROJECT_ENDPOINT: args.foundryEndpoint,
      AZURE_TENANT_ID: args.tenantId,
      AZURE_CLIENT_ID: args.clientId,
      RELAY_ALLOWED_ORIGINS: "http://localhost:3000",
      RELAY_PORT: "8790",
      M8T_INTERNAL_SECRET: internalSecret,
      WEB_INTERNAL_BASE_URL: args.webInternalBaseUrl,
    },
    // pnpm resolves to pnpm.cmd on Windows, which needs a shell to launch.
    shell: process.platform === "win32",
  });
  fs.closeSync(fd);
  if (child.pid == null) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_RELAY_SPAWN_FAILED",
      message: "Could not start the voice relay (pnpm did not spawn).",
      hint: "Check that pnpm + Node 20+ are installed; see the log at " + logPath + ".",
    });
  }
  child.unref();

  fs.writeFileSync(pidPath, String(child.pid), "utf8");

  const deadline = Date.now() + 45_000;
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  while (Date.now() < deadline) {
    await sleep(500);
    if (await isLocalPortOpen(portNum)) break;
  }

  return { alreadyRunning: false, logPath };
}

/** Best-effort browser open for the onboarding chat. Fire-and-forget: never
 *  throws, so a headless box still gets the printed URL fallback. */
export function autoOpenOnboardingUi(
  url: string,
  open: (u: string) => void | Promise<void> = tryOpenUrl,
): void {
  try {
    const result = open(url);
    if (result instanceof Promise) {
      result.catch(() => { /* best-effort */ });
    }
  } catch { /* best-effort */ }
}

/**
 * Stop the detached onboarding processes by killing their process groups.
 *
 * Returns true if a pid file was found (regardless of whether the process was
 * still alive), false if there was nothing to stop.
 */
function stopPidFile(pidPath: string): boolean {
  let pidStr: string;
  try {
    pidStr = fs.readFileSync(pidPath, "utf8").trim();
  } catch {
    return false; // no pid file — nothing to stop
  }

  const pid = parseManagedPid(pidStr);
  if (pid != null) {
    if (process.platform === "win32") {
      // Windows has no process-group signal; `/T` walks the tree so the
      // pnpm.cmd shim's actual child (dev server / relay) dies too. Best-effort:
      // a missing PID exits non-zero, which spawnSync surfaces without throwing.
      spawnSync("taskkill", ["/pid", String(pid), "/T", "/F"], { stdio: "ignore" });
    } else {
      try {
        // Negative pid = kill the entire process group (child was spawned detached).
        process.kill(-pid, "SIGTERM");
      } catch (e) {
        // ESRCH = process already gone — treat as success.
        if ((e as NodeJS.ErrnoException).code !== "ESRCH") throw e;
      }
    }
  }

  try { fs.unlinkSync(pidPath); } catch { /* already gone */ }
  return true;
}

export function stopOnboardingUi(home: string = os.homedir()): boolean {
  const uiStopped = stopPidFile(onboardingUiPaths(home).pidPath);
  const relayStopped = stopPidFile(onboardingRelayPaths(home).pidPath);
  return uiStopped || relayStopped;
}

/**
 * Assemble the intake deploy's fillable-field overrides. The inventory note is
 * OMITTED when absent (not set to an empty string) so `deployPromptAdvisor` falls
 * through to the persona frontmatter default — the feature's absence contract.
 */
export function buildIntakeFieldOverrides(args: {
  founderIdentityNote: string;
  chosenModelNote: string;
  inventoryNote?: string;
}): Record<string, string> {
  return {
    founder_identity_note: args.founderIdentityNote,
    chosen_model_note: args.chosenModelNote,
    ...(args.inventoryNote ? { subscription_inventory_note: args.inventoryNote } : {}),
  };
}
